import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "lib/api/api";
import AdminNavbar from "./AdminNavbar";

const emptyForm = { teacherId: "", studentName: "", studentGender: "", feedbackEn: "", feedbackDe: "", feedbackAr: "" };

const AdminTeacherFeedbackPage = () => {
  const [teachers, setTeachers] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ error: "", success: "" });

  const sortedTeachers = useMemo(() => [...teachers].sort((a, b) =>
    `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(`${b.firstName || ""} ${b.lastName || ""}`)), [teachers]);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage({ error: "", success: "" });
    try {
      const [feedbackResponse, teacherResponse] = await Promise.all([
        api.get("/admin/teacher-feedbacks"), api.get("/teachers/teacherDetails"),
      ]);
      setRows(feedbackResponse.data || []);
      setTeachers(teacherResponse.data || []);
    } catch (error) {
      setMessage({ error: error.response?.data?.message || "Could not load teacher feedbacks.", success: "" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const change = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const reset = () => { setForm(emptyForm); setEditingId(null); };

  const submit = async (event) => {
    event.preventDefault();
    setMessage({ error: "", success: "" });
    try {
      const payload = { ...form, teacherId: Number(form.teacherId) };
      if (editingId) await api.put(`/admin/teacher-feedbacks/${editingId}`, payload);
      else await api.post("/admin/teacher-feedbacks", payload);
      reset();
      setMessage({ error: "", success: editingId ? "Teacher feedback updated." : "Teacher feedback created." });
      const response = await api.get("/admin/teacher-feedbacks");
      setRows(response.data || []);
    } catch (error) { setMessage({ error: error.response?.data?.message || "Could not save teacher feedback.", success: "" }); }
  };

  const edit = (row) => {
    setEditingId(row.id);
    setForm({ teacherId: row.teacherId, studentName: row.studentName, studentGender: row.studentGender, feedbackEn: row.feedbackEn, feedbackDe: row.feedbackDe, feedbackAr: row.feedbackAr });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete the feedback from ${row.studentName}?`)) return;
    try {
      await api.delete(`/admin/teacher-feedbacks/${row.id}`);
      setRows((current) => current.filter((item) => item.id !== row.id));
      if (editingId === row.id) reset();
      setMessage({ error: "", success: "Teacher feedback deleted." });
    } catch (error) { setMessage({ error: error.response?.data?.message || "Could not delete teacher feedback.", success: "" }); }
  };

  return <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fb" }}><AdminNavbar /><Box sx={{ p: { xs: 2, md: 3 } }}>
    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}><Box><Typography variant="h4" fontWeight={900}>Teacher Feedbacks</Typography><Typography color="text.secondary">Assign multilingual student feedback to a teacher.</Typography></Box><Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button></Stack>
    {message.error && <Alert severity="error" sx={{ mb: 2 }}>{message.error}</Alert>}{message.success && <Alert severity="success" sx={{ mb: 2 }}>{message.success}</Alert>}
    <Paper component="form" onSubmit={submit} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>{editingId ? "Edit feedback" : "Add feedback"}</Typography>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <FormControl required fullWidth><InputLabel>Teacher</InputLabel><Select label="Teacher" value={form.teacherId} onChange={change("teacherId")}>{sortedTeachers.map((teacher) => <MenuItem key={teacher.teacherId} value={teacher.teacherId}>{teacher.firstName} {teacher.lastName}</MenuItem>)}</Select></FormControl>
          <TextField required fullWidth label="Student name" value={form.studentName} onChange={change("studentName")} inputProps={{ maxLength: 200 }} />
          <FormControl required fullWidth><InputLabel>Student gender</InputLabel><Select label="Student gender" value={form.studentGender} onChange={change("studentGender")}><MenuItem value="MALE">Male</MenuItem><MenuItem value="FEMALE">Female</MenuItem></Select></FormControl>
        </Stack>
        <TextField required multiline minRows={3} label="Feedback (English)" value={form.feedbackEn} onChange={change("feedbackEn")} />
        <TextField required multiline minRows={3} label="Feedback (German)" value={form.feedbackDe} onChange={change("feedbackDe")} />
        <TextField required multiline minRows={3} label="Feedback (Arabic)" value={form.feedbackAr} onChange={change("feedbackAr")} inputProps={{ dir: "rtl" }} sx={{ "& textarea": { textAlign: "right" } }} />
        <Stack direction="row" spacing={1}><Button type="submit" variant="contained">{editingId ? "Update" : "Create"}</Button>{editingId && <Button onClick={reset}>Cancel</Button>}</Stack>
      </Stack>
    </Paper>
    <Paper><TableContainer><Table><TableHead><TableRow><TableCell>Teacher</TableCell><TableCell>Student</TableCell><TableCell>Gender</TableCell><TableCell>English feedback</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead><TableBody>
      {!loading && rows.length === 0 && <TableRow><TableCell colSpan={5} align="center">No teacher feedbacks found.</TableCell></TableRow>}
      {rows.map((row) => <TableRow key={row.id} hover><TableCell>{row.teacherName}</TableCell><TableCell>{row.studentName}</TableCell><TableCell>{row.studentGender === "FEMALE" ? "Female" : "Male"}</TableCell><TableCell sx={{ maxWidth: 500, whiteSpace: "pre-wrap" }}>{row.feedbackEn}</TableCell><TableCell align="right"><Button size="small" onClick={() => edit(row)}>Edit</Button><Button size="small" color="error" onClick={() => remove(row)}>Delete</Button></TableCell></TableRow>)}
    </TableBody></Table></TableContainer></Paper>
  </Box></Box>;
};

export default AdminTeacherFeedbackPage;
